// Remplir le vrai formulaire (D-124), dans l'appareil de celui qui signe : le PDF officiel tel quel (public/cerfa/), ce que
// l'app y pose (src/domain/cerfa.ts), la signature du signataire dans « Date et signature ». pdf-lib est chargé à la
// demande (il ne pèse sur aucun autre écran). Rien ne sort de l'appareil ici : l'écran envoie ensuite le PDF signé et son
// empreinte (receipt_sign).
// Le PDF officiel n'est jamais réécrit : on ajoute par-dessus (texte, coches, image) et on remplit les champs du
// 2041-MEC-SD ; métadonnées d'origine gardées (updateMetadata: false).
import { planMec, planRd, MEC_16216_03, RD_11580_05, type CerfaData, type PlanText } from '../domain/cerfa';

export interface Drawn { page: number; key: string; text?: string; x: number; y: number; size?: number; kind: 'text' | 'check' | 'image' | 'field' }
export interface Filled { bytes: Uint8Array; drawn: Drawn[] }
export interface FillOptions {
  /** La signature (PNG), posée dans « Date et signature » ; sans elle : imprimé pour être signé à la main. */
  signature?: Uint8Array | null;
  /** Le club de démonstration : « EXEMPLE » en pied de chaque page. */
  demo?: boolean;
}

const INK = { r: 0.05, g: 0.2, b: 0.55 };
const SAMPLE = { r: 0.8, g: 0.1, b: 0.1 };

/** Le PDF officiel d'une édition, servi avec l'app. */
export async function officialPdf(file: string): Promise<Uint8Array> {
  const r = await fetch(`/cerfa/${file}`);
  if (!r.ok) throw new Error('formulaire introuvable');
  return new Uint8Array(await r.arrayBuffer());
}

/** Remplit le formulaire qui convient (2041-RD ou 2041-MEC-SD) ; `official` : les octets du PDF publié. */
export async function fillReceipt(official: Uint8Array, form: '2041-RD' | '2041-MEC-SD', data: CerfaData, opts: FillOptions = {}): Promise<Filled> {
  const lib = await import('pdf-lib');
  const { PDFDocument, StandardFonts, rgb, PDFName } = lib;
  const doc = await PDFDocument.load(official, { updateMetadata: false });
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const charset = new Set(font.getCharacterSet());
  // Ce que la police standard sait écrire (WinAnsi) : les accents restent, le reste se simplifie.
  const safe = (t: string) => [...t.replace(/[  ]/g, ' ')].map(ch => (charset.has(ch.codePointAt(0)!) ? ch
    : [...ch.normalize('NFKD')].filter(c => charset.has(c.codePointAt(0)!)).join('') || '?')).join('');
  const measure = (t: string, size: number) => font.widthOfTextAtSize(safe(t), size);
  const pages = doc.getPages();
  const drawn: Drawn[] = [];
  const color = rgb(INK.r, INK.g, INK.b);

  const write = (t: PlanText) => {
    const text = safe(t.text);
    let size = t.size;
    while (size > t.minSize && font.widthOfTextAtSize(text, size) > t.maxWidth) size = Math.round((size - 0.25) * 100) / 100;
    const w = font.widthOfTextAtSize(text, size);
    const x = t.align === 'right' ? t.x - w : t.align === 'center' ? t.x - w / 2 : t.x;
    pages[t.page]!.drawText(text, { x, y: t.y, size, font, color });
    drawn.push({ page: t.page, key: t.key, text, x: Math.round(x * 100) / 100, y: t.y, size, kind: 'text' });
  };
  const sign = async (area: { page: number; x: number; y: number; w: number; h: number }) => {
    if (!opts.signature) return;
    const img = await doc.embedPng(opts.signature);
    const k = Math.min(area.w / img.width, area.h / img.height);
    const w = img.width * k, h = img.height * k;
    const y = area.y + (area.h - h) / 2;
    pages[area.page]!.drawImage(img, { x: area.x, y, width: w, height: h });
    drawn.push({ page: area.page, key: 'signature', x: area.x, y, kind: 'image' });
  };

  if (form === '2041-RD') {
    const plan = planRd(data, measure);
    for (const t of plan.texts) write(t);
    for (const c of plan.checks) {
      const p = pages[c.page]!, h = c.half;
      p.drawLine({ start: { x: c.cx - h, y: c.cy - h }, end: { x: c.cx + h, y: c.cy + h }, thickness: 1.1, color });
      p.drawLine({ start: { x: c.cx - h, y: c.cy + h }, end: { x: c.cx + h, y: c.cy - h }, thickness: 1.1, color });
      drawn.push({ page: c.page, key: c.key, x: c.cx, y: c.cy, kind: 'check' });
    }
    await sign(plan.signature);
  } else {
    const plan = planMec(data, measure);
    const pdfForm = doc.getForm();
    for (const [name, value] of Object.entries(plan.fields)) {
      const tf = pdfForm.getTextField(name);
      const max = tf.getMaxLength();
      tf.setText(max ? safe(value).slice(0, max) : safe(value));
      drawn.push({ page: name.startsWith('a') ? 0 : 1, key: name, text: safe(value), x: 0, y: 0, kind: 'field' });
    }
    // Les cases : chaque widget, désigné par sa page, son nom et son état « coché » (des noms se répètent d'une page à l'autre).
    const pageOf = new Map(pages.map((p, i) => [p.ref.toString(), i]));
    const widgetsOn = new Set(plan.checks.map(c => `${c.page}:${c.name}:${c.on}`));
    for (const field of pdfForm.getFields()) {
      const af = field.acroField;
      const ws = af.getWidgets();
      for (const w of ws) {
        const on = w.getOnValue();
        if (!on) continue;
        const ref = w.P() ?? doc.context.getObjectRef(w.dict);
        let pageIndex = ref ? pageOf.get(ref.toString()) : undefined;
        if (pageIndex === undefined) {
          const wr = doc.context.getObjectRef(w.dict)?.toString();
          pageIndex = pages.findIndex(p => p.node.Annots()?.asArray().some(a => a.toString() === wr));
        }
        const key = `${pageIndex}:${field.getName()}:${on.decodeText()}`;
        if (widgetsOn.has(key)) {
          w.setAppearanceState(on);
          af.dict.set(PDFName.of('V'), on);
          const r = w.getRectangle();
          drawn.push({ page: pageIndex, key: `${field.getName()}=${on.decodeText()}`, x: r.x + r.width / 2, y: r.y + r.height / 2, kind: 'check' });
        }
      }
    }
    // Un champ dont une case est cochée : ses autres widgets restent « Off ».
    for (const field of pdfForm.getFields()) {
      const v = field.acroField.dict.get(PDFName.of('V'));
      for (const w of field.acroField.getWidgets()) {
        const on = w.getOnValue();
        if (on && v && on !== v && w.getAppearanceState() !== PDFName.of('Off')) w.setAppearanceState(PDFName.of('Off'));
      }
    }
    for (const t of plan.texts) write(t);
    await sign(plan.signature);
  }
  if (opts.demo) {
    for (const p of pages) p.drawText('EXEMPLE · données fictives (club de démonstration Arceau) : ce reçu n’a aucune valeur', { x: 40, y: 12, size: 7.5, font, color: rgb(SAMPLE.r, SAMPLE.g, SAMPLE.b) });
  }
  const bytes = await doc.save({ updateFieldAppearances: form === '2041-MEC-SD' });
  return { bytes, drawn };
}

/** Le formulaire d'une édition (les octets publiés, vérifiés par leur empreinte). */
export const EDITIONS = { '2041-RD': RD_11580_05, '2041-MEC-SD': MEC_16216_03 } as const;

/** L'empreinte SHA-256 (hexadécimal) : le registre garde celle du reçu tel qu'il est parti. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(d)].map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Base64 d'octets (le PDF envoyé à la base) et l'inverse. */
export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
export function fromBase64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/** Plusieurs reçus en un seul PDF (« Télécharger les reçus »), dans l'ordre donné. */
export async function mergePdfs(files: Uint8Array[]): Promise<Uint8Array> {
  const { PDFDocument } = await import('pdf-lib');
  const out = await PDFDocument.create();
  for (const f of files) {
    const src = await PDFDocument.load(f, { updateMetadata: false });
    const pages = await out.copyPages(src, src.getPageIndices());
    for (const p of pages) out.addPage(p);
  }
  return out.save();
}
